import { Button } from '@progress/kendo-react-buttons';
import { FieldRenderProps, FieldWrapper } from '@progress/kendo-react-form';
import { Hint, Label, Error } from '@progress/kendo-react-labels';
import { arrowsRotateIcon } from '@progress/kendo-svg-icons';
import React, { useRef } from 'react';
import { FieldLoadingWrapper } from './FieldLoadingWrapper';

export interface FormFieldWrapperProps extends FieldRenderProps {
  children: React.ReactNode;
  tooltipMessage?: string;
  isLoading?: boolean;
  showRefreshButton?: boolean;
  onRefresh?: () => void;
  leadingActionButtons?: React.ReactNode;
}

const getValidationState = (
  touched: boolean,
  modified: boolean,
  visited: boolean,
  validationMessage: string | null | undefined,
  id: string
) => {
  const shouldShow = (touched || modified || visited) && validationMessage;
  return {
    showValidationMessage: !!shouldShow,
    validationMessage: shouldShow ? validationMessage : null,
    errorId: shouldShow ? `${id}_error` : '',
    errorLabelClass: shouldShow ? 'k-text-error' : ''
  };
};

const getHintState = (showValidationMessage: boolean, hint: string, id: string) => {
  const shouldShow = !showValidationMessage && hint;
  return {
    showHint: shouldShow,
    hintId: shouldShow ? `${id}_hint` : ''
  };
};

const formatLabel = (label: string, optional: boolean) => `${label}${optional === false ? ' *' : ''}`;

export const FormFieldWrapper: React.FC<FormFieldWrapperProps> = props => {
  const {
    validationMessage,
    value,
    touched,
    id,
    valid,
    disabled,
    hint,
    label,
    wrapperStyle,
    optional,
    modified,
    visited,
    tooltipMessage,
    isLoading = false,
    children,
    showRefreshButton,
    onRefresh,
    leadingActionButtons
  } = props;

  const editorRef = useRef<HTMLDivElement>(null);

  const validation = getValidationState(touched, modified, visited, validationMessage, id);
  const hintState = getHintState(validation.showValidationMessage, hint, id);
  const labelId = label ? `${id}_label` : '';
  const shouldShowActionButtons = Boolean(leadingActionButtons) || showRefreshButton;

  return (
    <FieldWrapper style={wrapperStyle}>
      {label && (
        <div title={tooltipMessage}>
          <Label
            id={labelId}
            editorRef={editorRef}
            editorId={id}
            editorValid={valid}
            editorDisabled={disabled}
            className={`k-form-label ${validation.errorLabelClass}`}
          >
            {formatLabel(label, optional)}
          </Label>
        </div>
      )}

      <div className="k-form-field-wrap" ref={editorRef}>
        <FieldLoadingWrapper
          isLoading={isLoading}
          actionButtons={
            shouldShowActionButtons ? (
              <div className="cpsFieldActionButtons">
                <>
                  {showRefreshButton && (
                    <Button
                      type="button"
                      svgIcon={arrowsRotateIcon}
                      size="small"
                      fillMode="flat"
                      onClick={onRefresh}
                      className="cpsFieldActionButtonCompact"
                      title="Refresh"
                      aria-label="Refresh"
                      disabled={disabled || !value}
                    />
                  )}
                  {leadingActionButtons}
                </>
              </div>
            ) : undefined
          }
        >
          {children}
        </FieldLoadingWrapper>

        {hintState.showHint && <Hint id={hintState.hintId}>{hint}</Hint>}
        {validation.showValidationMessage && <Error id={validation.errorId}>{validationMessage}</Error>}
      </div>
    </FieldWrapper>
  );
};
